import React, { createContext, useContext, useState, useEffect } from 'react';
import { trpc } from '@/trpc/client';

type Season = 'none' | 'winter' | 'halloween' | 'valentine' | 'stpats';

interface SeasonContextType {
    season: Season;
    isAutoMode: boolean;
    refetchSettings: () => void;
}

const SeasonContext = createContext<SeasonContextType | undefined>(undefined);

export const SeasonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Default fallback
    const [season, setSeason] = useState<Season>('none');
    const [isAutoMode, setAutoMode] = useState(true);

    const { data, refetch } = trpc.system.getPublicSettings.useQuery(undefined, {
        refetchOnWindowFocus: true, // Auto-refresh when checking
        staleTime: 60 * 1000,
    });

    useEffect(() => {
        if (!data) return;

        if (data.seasonMode === 'manual') {
            setAutoMode(false);
            setSeason(data.currentSeason as Season);
        } else {
            setAutoMode(true);
            // Default Auto Logic (Client-side fallback or could be moved to backend)
            // Keeping client-side auto logic for responsiveness
            calculateAutoSeason();
        }
    }, [data]);

    const calculateAutoSeason = () => {
        const date = new Date();
        const month = date.getMonth(); // 0-11
        const day = date.getDate();

        if (month === 11 || month === 0) { // Dec-Jan
            setSeason('winter');
        } else if (month === 9 && day > 20) { // Late Oct
            setSeason('halloween');
        } else if (month === 1 && day === 14) { // Feb 14
            setSeason('valentine');
        } else if (month === 2 && day === 17) { // Mar 17
            setSeason('stpats');
        } else {
            setSeason('none');
        }
    };

    return (
        <SeasonContext.Provider value={{ season, isAutoMode, refetchSettings: refetch }}>
            {children}
        </SeasonContext.Provider>
    );
};

export const useSeason = () => {
    const context = useContext(SeasonContext);
    if (!context) throw new Error("useSeason must be used within a SeasonProvider");
    return context;
};
